import { flg } from "@aix/webutils/src/ui/logging"
import { sanitizeLogRecord } from "@aix/jsutils/src/axiom-events/sanitize"
import { AIX_ENV, EAixEnv, POSTHOG_KEY } from "~/lib/env"
import posthog from "posthog-js"
import type { EVMAnalyticsEvent, EVMAnalyticsProperties } from "@aix/vidmax-shared/src/analytics"
import { VM_ANALYTICS_CONTRACT_VERSION } from "@aix/vidmax-shared/src/analytics"
import { applyStoredFeatureFlagOverrides, setFeatureFlagOverrides } from "~/utils/featureFlagOverrides"

/** Full referrer URLs can carry another site's private path or query; only their domain is kept */
const privateNavigationProperties = ["$referrer", "$initial_referrer", "$session_entry_referrer"]
const pathProperties = ["$pathname", "$initial_pathname", "$session_entry_pathname", "$prev_pageview_pathname"]
const urlProperties = ["$current_url", "$initial_current_url", "$session_entry_url"]
const domainProperties = ["$referring_domain", "$initial_referring_domain", "$session_entry_referring_domain"]

const ID_PLACEHOLDER = ":id"
// Explicit analytics allowlist: only public route literals survive. New routes fail closed until added.
const ANALYTICS_ROUTES = [
  "/",
  "/$modelSlug",
  "/404",
  "/account",
  "/ad-library",
  "/api-keys",
  "/asset-library",
  "/audio",
  "/automated",
  "/automated-create",
  "/b-roll",
  "/billing",
  "/brands",
  "/changelog",
  "/creator",
  "/deletion-request",
  "/developers",
  "/docs",
  "/home",
  "/hooks",
  "/how-it-works",
  "/media-space",
  "/pdf",
  "/pricing",
  "/privacy",
  "/products",
  "/schedule",
  "/sentry-break",
  "/signout",
  "/social-accounts",
  "/storyboard-exploration",
  "/terms",
  "/ugc-ai-avatar",
  "/usage",
  "/voices",
  "/webhooks",
  "/ad-library/$adId",
  "/admin/ad-library",
  "/admin/ads-performance",
  "/admin/asset-jobs",
  "/admin/assets",
  "/admin/audit",
  "/admin/avatars",
  "/admin/benchmarks",
  "/admin/brands",
  "/admin/content-seeds",
  "/admin/editor-effects",
  "/admin/email",
  "/admin/evals",
  "/admin/experiments",
  "/admin/feedback",
  "/admin/kev",
  "/admin/mastra",
  "/admin/model-pricing",
  "/admin/model-registry",
  "/admin/pipelines",
  "/admin/presets",
  "/admin/products",
  "/admin/render-lab",
  "/admin/scheduledPosts",
  "/admin/series",
  "/admin/signin",
  "/admin/static-ads",
  "/admin/teams",
  "/admin/tools",
  "/admin/users",
  "/admin/videos",
  "/admin/voices",
  "/ads/static",
  "/asset-library/$assetId",
  "/auth/callback",
  "/auth/signin",
  "/auth/signout",
  "/auth/verify",
  "/blog/$slug",
  "/blog/admin",
  "/create/ad-studio",
  "/create/audio",
  "/create/captions",
  "/create/edit",
  "/create/image",
  "/create/ugc",
  "/create/ugc-avatars",
  "/create/ugc-templates",
  "/create/video",
  "/embed/edit",
  "/media-space/boards",
  "/media-space/workflows",
  "/series/$seriesId",
  "/team/invite",
  "/templates/ai-story",
  "/templates/fake-text",
  "/templates/genz-avatars",
  "/templates/pov-story",
  "/templates/reddit-story",
  "/tools/$id",
  "/tools/ad-scraper",
  "/tools/ai-avatar",
  "/tools/ai-sound-effects",
  "/tools/ai-voiceover",
  "/tools/asset-library",
  "/tools/avatar-ad-generator",
  "/tools/avatar-library",
  "/tools/background-remover",
  "/tools/image-resizer",
  "/tools/image-transformer",
  "/tools/image-upscaler",
  "/tools/infinitetalk-demo",
  "/tools/prompt-hub",
  "/tools/script-writer",
  "/tools/style-transfer",
  "/tools/veo3-generator",
  "/tools/video-effects",
  "/v/$linkId",
  "/viral/pricing",
  "/admin",
  "/auth",
  "/blog",
  "/create",
  "/media-space/",
  "/success-stories",
  "/team",
  "/templates",
  "/tools",
  "/tutorial",
  "/videos",
  "/viral",
  "/admin/ad-engine/$videoId",
  "/admin/ad-engine/formats",
  "/admin/ad-engine/review",
  "/admin/ads-performance/meta-callback",
  "/admin/asset-jobs/$id",
  "/admin/pipelines/$executionId",
  "/admin/team/$id",
  "/admin/trace/$videoId",
  "/auth/error/$",
  "/create/captions/$videoId",
  "/media-space/asset/$assetId",
  "/media-space/board/$boardId",
  "/prototypes/carousels/$carouselId",
  "/prototypes/image-editor/$imageEditId",
  "/videos/$videoId/debug",
  "/videos/$videoId/editor",
  "/videos/$videoId/storyboard",
  "/admin/ad-engine",
  "/prototypes/carousels",
  "/prototypes/image-editor",
  "/prototypes/manwha",
  "/prototypes/social-monitor",
  "/videos/$videoId",
  "/videos/$videoId/editor/fake-text",
  "/videos/$videoId/editor/genz-avatars",
  "/videos/$videoId/editor/reddit-story",
  "/videos/$videoId/editor/",
  "/u/$id",
  "/share/$id",
  "/videos/$videoId/edit",
  "/search",
].map((route) => route.split("/"))

export const normalizeAnalyticsPath = (path: string) => {
  const pathname = path.split(/[?#]/, 1)[0] ?? "/"
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return "/"
  const segments = pathname.split("/")
  return (
    segments
      .map((segment, index) => {
        if (!segment) return ""
        const knownLiteral = ANALYTICS_ROUTES.some(
          (route) =>
            route[index] === segment &&
            !segment.startsWith("$") &&
            route.slice(0, index + 1).every((part, position) => part.startsWith("$") || part === segments[position]),
        )
        return knownLiteral ? segment : ID_PLACEHOLDER
      })
      .join("/") || "/"
  )
}

/** Origin + normalized path; the query and hash (tokens, prompts, emails) never leave the browser */
const normalizeAnalyticsUrl = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  try {
    const url = new URL(value)
    if (url.protocol !== "https:" && url.protocol !== "http:") return undefined
    return `${url.origin}${normalizeAnalyticsPath(url.pathname)}`
  } catch {
    // Not a URL; drop it rather than send it unscrubbed
    return undefined
  }
}

const HOSTNAME = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
const MAX_HOSTNAME_LENGTH = 253
const textProperty = (value: unknown) => (typeof value === "string" ? value : undefined)

/** Page and source fields, rebuilt from the raw values in a form that can't carry private content */
const safeNavigationProperties = (raw: Record<string, unknown>) => {
  const safe: Record<string, string> = {}
  for (const key of pathProperties) {
    const path = textProperty(raw[key])
    if (path) safe[key] = normalizeAnalyticsPath(path)
  }
  for (const key of urlProperties) {
    const url = normalizeAnalyticsUrl(raw[key])
    if (url) safe[key] = url
  }
  for (const key of domainProperties) {
    const domain = textProperty(raw[key])
    if (domain && (domain === "$direct" || (domain.length <= MAX_HOSTNAME_LENGTH && HOSTNAME.test(domain))))
      safe[key] = domain
  }
  return safe
}

const scrubNavigation = (raw: object): Record<string, unknown> => {
  const safe = sanitizeLogRecord(raw)
  for (const key of [...privateNavigationProperties, ...pathProperties, ...urlProperties, ...domainProperties]) {
    delete safe[key]
  }
  return { ...safe, ...safeNavigationProperties(raw as Record<string, unknown>) }
}

const sanitizeAnalyticsProperties = (properties: object): Record<string, unknown> => {
  const safe = scrubNavigation(properties)
  for (const key of ["$set", "$set_once"]) {
    const person = (properties as Record<string, unknown>)[key]
    if (person && typeof person === "object" && !Array.isArray(person)) safe[key] = scrubNavigation(person)
  }
  return safe
}

export const POSTHOG_ENABLED = true
const POSTHOG_API_HOST = AIX_ENV === EAixEnv.LOCAL ? "https://us.i.posthog.com" : "/ingest"

/**
 * VM-960: session-replay sampling rate (0–1). 1.0 = record every session.
 * Kept at full coverage while we actively watch the W30 funnel replays; lower this
 * (e.g. 0.25) once funnel analysis wraps to cut PostHog recording cost. This takes
 * precedence over the project's remote sampling setting.
 */
const SESSION_REPLAY_SAMPLE_RATE = 1.0

/** Feature Flags */
// import { useFeatureFlagEnabled } from "posthog-js/react";
// const flagEnabled = useFeatureFlagEnabled("test-flag");

// Track initialization state
let posthogInitialized = false

/**
 * Initialize PostHog - deferred to avoid blocking initial render
 * Called after first idle period or after 2 seconds max
 */
export const initPostHog = () => {
  if (posthogInitialized || typeof window === "undefined") return
  posthogInitialized = true

  try {
    if (POSTHOG_KEY) {
      posthog.init(POSTHOG_KEY, {
        capture_pageview: false,
        api_host: POSTHOG_API_HOST,
        ui_host: "https://us.posthog.com",
        disable_session_recording: true,
        // Private content replay disabled; masking remains defensive if explicitly re-enabled.
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
          maskInputOptions: { password: true, email: true },
          sampleRate: SESSION_REPLAY_SAMPLE_RATE,
        },
        autocapture: false,
        before_send: (event) =>
          event === null
            ? null
            : {
                ...event,
                properties: { ...sanitizeAnalyticsProperties(event.properties), token: event.properties.token },
                ...(event.$set && { $set: sanitizeAnalyticsProperties(event.$set) }),
                ...(event.$set_once && { $set_once: sanitizeAnalyticsProperties(event.$set_once) }),
              },
        // Disable surveys to prevent loading surveys.js bundle (32KB) on every page
        // Enable surveys only on specific pages that need them
        disable_surveys: true,
        loaded: (client) => {
          flg.info("PostHog loaded successfully")
          client.register({ environment: AIX_ENV })
          applyStoredFeatureFlagOverrides()
        },
      })
    } else {
      flg.warn("PostHog key not found in environment variables")
    }
  } catch (_error) {
    // PostHog init failed — analytics non-critical
    return
  }
}

interface IPageViewProperties {
  $current_url: string
}

type PostHogCaptureArgs<E extends EVMAnalyticsEvent> =
  Record<string, never> extends EVMAnalyticsProperties[NoInfer<E>]
    ? [data?: EVMAnalyticsProperties[NoInfer<E>]]
    : [data: EVMAnalyticsProperties[NoInfer<E>]]

interface ICapturePostHogEvent {
  <E extends EVMAnalyticsEvent>(event: E, ...args: PostHogCaptureArgs<E>): void
  (event: "$pageview", data: IPageViewProperties): void
}

export const capturePostHogEvent: ICapturePostHogEvent = (event: EVMAnalyticsEvent | "$pageview", data?: object) => {
  if (!posthog || !event) {
    flg.warn("PostHog not available or event not provided")
    return
  }
  // Ensure init before capturing. Init is deferred to idle (≤2s), so eager
  // captures would otherwise be dropped before posthog.init() runs. (VM-958)
  if (!posthogInitialized) initPostHog()
  const formattedData = {
    ...sanitizeAnalyticsProperties(data ?? {}),
    environment: AIX_ENV,
    contract_version: VM_ANALYTICS_CONTRACT_VERSION,
  }

  if (POSTHOG_ENABLED) {
    try {
      posthog.capture(event, formattedData)
    } catch (_error) {
      // PostHog capture failed — analytics non-critical
      return
    }
  }
}

const identifyPostHogUser = (userId: string, properties?: Record<string, unknown>) => {
  try {
    if (!posthogInitialized) initPostHog()
    posthog.identify(userId, sanitizeAnalyticsProperties(properties ?? {}))
  } catch (_error) {
    // PostHog identity failed — analytics non-critical
    return
  }
}

export const resetPostHogIdentity = () => {
  try {
    setFeatureFlagOverrides({})
    posthog.reset()
    posthog.register({ environment: AIX_ENV, contract_version: VM_ANALYTICS_CONTRACT_VERSION })
  } catch (_error) {
    // PostHog reset failed — logout must still continue
    return
  }
}

export const usePH = () => {
  return {
    capture: capturePostHogEvent,
    identify: identifyPostHogUser,
    posthog,
    resetIdentity: resetPostHogIdentity,
  }
}
